'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { useCachedProjectsSidebarCreationVariation } from '#pplx/frontend/apps/ask/generated/assignment-hooks/useCachedProjectsSidebarCreationVariation.js';
import { useSuccessfulDeletedCollectionUuids } from '#pplx/frontend/apps/ask/hooks/collections-hooks/collection-mutations/deleteCollectionMutationState.js';
import { routes } from '#pplx/frontend/apps/ask/routing/spaRoutes.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { useRouter } from '#pplx/frontend/router/provider.js';
import { usePathname } from '#pplx/frontend/router/usePathname.js';
import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
const SidebarProjectCreationContext = createContext(null);
const EMPTY_ANCHORED_PROJECTS = [];
export const SidebarProjectCreationProvider = ({ children, openCreateSpaceModal }) => {
    const pathname = usePathname();
    const router = useRouter();
    const { session, status: sessionStatus } = useSession();
    const sessionUserId = sessionStatus === 'authenticated' ? session.user.id : null;
    // Read the per-account cached assignment for this load; fresh assignments apply next full load.
    const isInlineProjectCreationEnabled = useCachedProjectsSidebarCreationVariation({
        defaultValue: false,
    });
    // This provider sits above the open/collapsed and desktop/mobile sidebar trees so their ordinary
    // replacement cannot discard a draft or a server-confirmed project's within-session slot.
    const [inlineCreate, setInlineCreate] = useState(null);
    const [anchoredProjects, setAnchoredProjects] = useState(null);
    const successfulDeletedProjectUuids = useSuccessfulDeletedCollectionUuids();
    // A route version records departure even if the user returns before creation finishes.
    const routeIntentRef = useRef({ pathname, version: 0 });
    const submissionRef = useRef(null);
    const inlineProjectCreationEnabledRef = useRef(isInlineProjectCreationEnabled);
    const operationRef = useRef(null);
    // Identity objects change on every account transition, so A → B → A cannot revive old work.
    const sessionIdentityRef = useRef({ generation: 0, userId: sessionUserId });
    if (sessionIdentityRef.current.userId !== sessionUserId) {
        sessionIdentityRef.current = {
            generation: sessionIdentityRef.current.generation + 1,
            userId: sessionUserId,
        };
        operationRef.current = null;
        submissionRef.current = null;
    }
    if (inlineProjectCreationEnabledRef.current !== isInlineProjectCreationEnabled) {
        operationRef.current = null;
        submissionRef.current = null;
    }
    inlineProjectCreationEnabledRef.current = isInlineProjectCreationEnabled;
    useLayoutEffect(() => {
        if (routeIntentRef.current.pathname === pathname)
            return;
        routeIntentRef.current = {
            pathname,
            version: routeIntentRef.current.version + 1,
        };
        setInlineCreate((current) => current?.focusRequested ? { ...current, focusRequested: false } : current);
    }, [pathname]);
    const sessionIdentity = sessionIdentityRef.current;
    useLayoutEffect(() => {
        setInlineCreate((current) => current?.operation.identity === sessionIdentity ? current : null);
        setAnchoredProjects((current) => (current?.identity === sessionIdentity ? current : null));
    }, [sessionIdentity]);
    useEffect(() => {
        if (isInlineProjectCreationEnabled)
            return;
        setInlineCreate((current) => (current?.status.kind === 'pending' ? current : null));
        setAnchoredProjects(null);
    }, [isInlineProjectCreationEnabled]);
    useEffect(() => {
        if (successfulDeletedProjectUuids.length === 0)
            return;
        const deletedProjectUuids = new Set(successfulDeletedProjectUuids);
        setAnchoredProjects((current) => {
            if (!current || current.identity !== sessionIdentityRef.current)
                return current;
            const retainedProjects = current.projects.filter((project) => !deletedProjectUuids.has(project.uuid));
            return retainedProjects.length === current.projects.length
                ? current
                : { ...current, projects: retainedProjects };
        });
    }, [successfulDeletedProjectUuids]);
    const startInlineCreate = useCallback((entryPoint) => {
        const identity = sessionIdentityRef.current;
        if (!inlineProjectCreationEnabledRef.current || identity.userId === null)
            return;
        // Reopening the same draft keeps its operation; dismissal or identity changes invalidate it.
        const currentOperation = operationRef.current;
        const operation = currentOperation?.identity === identity ? currentOperation : { identity };
        operationRef.current = operation;
        setInlineCreate((current) => current?.operation === operation
            ? { ...current, focusRequested: true }
            : {
                entryPoint,
                focusRequested: true,
                status: { kind: 'draft' },
                operation,
                title: '',
            });
    }, []);
    const isOperationCurrent = useCallback((operation) => {
        return (operation !== null &&
            operationRef.current === operation &&
            operation.identity === sessionIdentityRef.current &&
            operation.identity.userId !== null &&
            inlineProjectCreationEnabledRef.current);
    }, []);
    const activeOperation = isOperationCurrent(inlineCreate?.operation ?? null)
        ? (inlineCreate?.operation ?? null)
        : null;
    const operationCallbacks = useMemo(() => {
        const operation = activeOperation;
        const clearOperation = () => {
            if (operationRef.current === operation)
                operationRef.current = null;
            if (submissionRef.current?.operation === operation)
                submissionRef.current = null;
        };
        const updateOperation = (transition) => {
            setInlineCreate((current) => current?.operation === operation ? transition(current) : current);
        };
        return {
            dismiss: () => {
                if (!isOperationCurrent(operation) || submissionRef.current?.operation === operation) {
                    return false;
                }
                clearOperation();
                setInlineCreate((current) => (current?.operation === operation ? null : current));
                return true;
            },
            setTitle: (title) => {
                if (!isOperationCurrent(operation) || submissionRef.current?.operation === operation)
                    return;
                updateOperation((current) => ({ ...current, title, status: { kind: 'draft' } }));
            },
            showNameRequired: () => {
                if (!isOperationCurrent(operation) || submissionRef.current?.operation === operation)
                    return;
                updateOperation((current) => ({
                    ...current,
                    status: { kind: 'error', error: 'nameRequired' },
                }));
            },
            requestFocus: () => {
                if (isOperationCurrent(operation)) {
                    updateOperation((current) => ({ ...current, focusRequested: true }));
                }
            },
            clearFocusRequest: () => {
                if (isOperationCurrent(operation)) {
                    updateOperation((current) => ({ ...current, focusRequested: false }));
                }
            },
            beginSubmission: () => {
                // The owner closes the same-tick submission gap even if the editor remounts.
                if (!operation || !isOperationCurrent(operation) || submissionRef.current)
                    return false;
                submissionRef.current = { operation, routeVersion: routeIntentRef.current.version };
                updateOperation((current) => ({ ...current, status: { kind: 'pending' } }));
                return true;
            },
            finishSubmission: (result) => {
                const shouldApplyCompletion = isOperationCurrent(operation);
                const shouldNavigate = shouldApplyCompletion &&
                    submissionRef.current?.operation === operation &&
                    submissionRef.current.routeVersion === routeIntentRef.current.version;
                if (submissionRef.current?.operation === operation)
                    submissionRef.current = null;
                if (!shouldApplyCompletion || 'error' in result) {
                    updateOperation((current) => ({
                        ...current,
                        status: shouldApplyCompletion && 'error' in result
                            ? { kind: 'error', error: result.error }
                            : { kind: 'draft' },
                    }));
                    return;
                }
                clearOperation();
                setInlineCreate((current) => (current?.operation === operation ? null : current));
                if (!operation)
                    return;
                const { collection } = result;
                setAnchoredProjects((current) => ({
                    identity: operation.identity,
                    projects: [
                        collection,
                        ...(current?.identity === operation.identity ? current.projects : []).filter((project) => project.uuid !== collection.uuid),
                    ],
                }));
                if (shouldNavigate) {
                    router.push(routes['/spaces/:slug'].href({ path: { slug: collection.uuid } }));
                }
            },
            isCurrent: () => isOperationCurrent(operation),
        };
    }, [activeOperation, isOperationCurrent, router]);
    const visibleInlineCreate = activeOperation ? inlineCreate : null;
    const visibleAnchoredProjects = anchoredProjects?.identity === sessionIdentity
        ? anchoredProjects.projects
        : EMPTY_ANCHORED_PROJECTS;
    const isInlineCreatePendingForIdentity = inlineCreate?.operation.identity === sessionIdentity && inlineCreate.status.kind === 'pending';
    const value = useMemo(() => ({
        anchoredProjects: isInlineProjectCreationEnabled
            ? visibleAnchoredProjects
            : EMPTY_ANCHORED_PROJECTS,
        editor: isInlineProjectCreationEnabled && visibleInlineCreate
            ? { draft: visibleInlineCreate, ...operationCallbacks }
            : null,
        openCreateSpaceModal,
        projectCreate: isInlineProjectCreationEnabled
            ? { kind: 'inline', disabled: isInlineCreatePendingForIdentity }
            : {
                kind: 'modal',
                open: openCreateSpaceModal,
                disabled: isInlineCreatePendingForIdentity,
            },
        startInlineCreate,
    }), [
        visibleAnchoredProjects,
        isInlineCreatePendingForIdentity,
        isInlineProjectCreationEnabled,
        openCreateSpaceModal,
        operationCallbacks,
        startInlineCreate,
        visibleInlineCreate,
    ]);
    return (_jsx(SidebarProjectCreationContext.Provider, { value: value, children: children }));
};
// eslint-disable-next-line react/only-export-components -- The provider and its typed hook are one context API.
export function useSidebarProjectCreation() {
    const context = useContext(SidebarProjectCreationContext);
    if (!context) {
        throw new Error('useSidebarProjectCreation must be used within SidebarProjectCreationProvider');
    }
    return context;
}
//# sourceMappingURL=SidebarProjectCreationContext.js.map